import { trpc } from "@/trpc/client";
import { clearTrpcSession } from "@/trpc/calls";
import {
  accountSessionMarker,
  adoptedGuestJwt,
  dropGuestTokenForAccountSession,
  guestAccessFromToken,
  guestAccessFromUserId,
} from "@/utils/guestAccess";

const TRPC_SESSION = "trpc";
const REPORTS_SESSION = "reportsSessionId";

function markTrpcSession() {
  if (typeof window === "undefined") return;
  window.localStorage.setItem("sessionId", TRPC_SESSION);
  if (window.localStorage.getItem("redirect") == null) {
    window.localStorage.setItem("redirect", "null");
  }
}

function storeReportsSession(sessionId) {
  if (typeof window === "undefined" || !sessionId) return;
  window.localStorage.setItem(REPORTS_SESSION, sessionId);
}

function firstLoginOrOk(result) {
  if (result?.status === "first_login" || result?.message === "FIRST_LOGIN_REDIRECT") {
    return { code: "403", message: "FIRST_LOGIN_REDIRECT" };
  }
  markTrpcSession();
  storeReportsSession(result?.session_id);
  return { code: "200", message: result?.message || "ok", user: result?.user || null };
}

function shapeLoggedUser(me) {
  const user = me?.user || {};
  const guest = guestAccessFromUserId(user.user_id);
  return {
    ...user,
    site_id: guest?.site_id ?? user.site_id ?? null,
    redirect: null,
    campaign_id: guest?.campaign_id ?? null,
    account: user.account_id == null
      ? null
      : { account_id: user.account_id, account_name: "" },
    count_unread_notifications: { count: 0 },
  };
}

const login = async (variables) => {
  const result = await trpc.auth.login.mutate({
    email: variables.userEmail,
    password: variables.userPassword,
  });
  return firstLoginOrOk(result);
};

const logout = async () => {
  if (typeof window !== "undefined") {
    // Sem o JWT guest, o logout manda o sid da conta e a API consegue revogá-lo.
    window.localStorage.removeItem("analytics_jwt");
  }
  await clearTrpcSession();
  if (typeof window !== "undefined") {
    window.localStorage.removeItem("sessionId");
    window.localStorage.removeItem(REPORTS_SESSION);
    window.localStorage.removeItem("analytics_jwt");
  }
  return { code: "200" };
};

const sendCode = async (userEmail) => {
  const result = await trpc.auth.sendCode.mutate({ email: userEmail });
  if (result?.status === "first_login") {
    return { code: "403", message: "FIRST_LOGIN_REDIRECT" };
  }
  return { code: "200", message: result?.message || "Código enviado" };
};

const insertCode = async (variables) => {
  const result = await trpc.auth.insertCode.mutate({
    email: variables.userEmail,
    code: variables.code,
  });
  return firstLoginOrOk(result);
};

const loggedUser = async () => {
  const token = typeof window !== "undefined" ? window.localStorage.getItem("analytics_jwt") : null;
  const guestToken = token && guestAccessFromToken(token) ? token : null;
  const adopted = adoptedGuestJwt();
  if (guestToken && accountSessionMarker() && !adopted) {
    dropGuestTokenForAccountSession();
  } else if (guestToken) {
    await trpc.auth.exchangeSiteToken.mutate({ token: guestToken });
  }
  const me = await trpc.auth.me.query();
  if (!adoptedGuestJwt()) {
    storeReportsSession(me?.session_id);
  }
  return shapeLoggedUser(me);
};

const recoverPassword = async (variables) => {
  const email = variables?.userEmail || variables?.email || variables?.content?.email;
  const token = variables?.token || variables?.content?.token;
  const password = variables?.password || variables?.userPassword || variables?.content?.password;
  if (token && password) {
    return trpc.auth.recoverPasswordReset.mutate({ token, password });
  }
  return trpc.auth.recoverPasswordRequest.mutate({ email: String(email || "") });
};

export default {
  login,
  sendCode,
  insertCode,
  loggedUser,
  recoverPassword,
  logout,
};
